<script>
import { GlCollapse, GlAlert, GlCollapsibleListbox, GlSprintf, GlLink } from '@gitlab/ui';
import glFeatureFlagMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import { s__ } from '~/locale';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import { DASHBOARD_TYPE_GROUP, DASHBOARD_TYPE_PROJECT } from 'ee/security_dashboard/constants';
import BulkChangeStatus from './bulk_change_status.vue';
import BulkChangeSeverity from './bulk_change_severity.vue';
import AttachExistingIssue from './attach_existing_issue.vue';
import AttachNewIssue from './attach_new_issue.vue';

export default {
  components: {
    GlCollapse,
    GlAlert,
    GlCollapsibleListbox,
    GlSprintf,
    GlLink,
    BulkChangeStatus,
    BulkChangeSeverity,
    AttachExistingIssue,
    AttachNewIssue,
  },
  mixins: [glFeatureFlagMixin()],
  inject: {
    dashboardType: {
      required: true,
    },
  },
  props: {
    selectedVulnerabilities: {
      type: Array,
      required: true,
    },
    visible: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      rejectedVulnerabilities: [],
      selectedAction: null,
    };
  },
  computed: {
    hasError() {
      return this.rejectedVulnerabilities.length > 0;
    },
    showActionDropdown() {
      return this.actions.length > 1;
    },
    toggleText() {
      const action = this.actions.find((item) => item.value === this.selectedAction);
      return action?.text || s__('SecurityReports|Select action');
    },
    canChangeSeverity() {
      return !this.glFeatures.hideVulnerabilitySeverityOverride;
    },
    canAttachExisting() {
      return this.isProjectOrGroup;
    },
    canAttachNew() {
      return this.isProjectOrGroup;
    },
    showBulkChangeStatus() {
      return this.actions.length === 1 || this.selectedAction === 'status';
    },
    showChangeSeverity() {
      return this.selectedAction === 'severity';
    },
    showAttachExisting() {
      return this.selectedAction === 'attach_existing';
    },
    showAttachNew() {
      return this.selectedAction === 'attach_new';
    },
    isProjectOrGroup() {
      return (
        this.dashboardType === DASHBOARD_TYPE_GROUP || this.dashboardType === DASHBOARD_TYPE_PROJECT
      );
    },
    actions() {
      const actions = [
        {
          text: s__('SecurityReports|Change status'),
          value: 'status',
        },
      ];
      if (this.canChangeSeverity) {
        actions.push({ text: s__('SecurityReports|Change severity'), value: 'severity' });
      }
      if (this.canAttachExisting) {
        actions.push({
          text: s__('SecurityReports|Attach to existing issue'),
          value: 'attach_existing',
        });
      }
      if (this.canAttachNew) {
        actions.push({
          text: s__('SecurityReports|Attach to new issue'),
          value: 'attach_new',
        });
      }
      return actions;
    },
  },
  methods: {
    cancel() {
      this.$emit('cancel-selection');
      this.selectedAction = null;
      this.rejectedVulnerabilities = [];
    },
    handleRejected(vulnerabilities) {
      this.rejectedVulnerabilities.push(...vulnerabilities);
    },
  },
  i18n: {
    selected: s__('SecurityReports|%{count} Selected'),
    vulnerabilitiesUpdateFailed: s__(
      'SecurityReports|Failed updating vulnerabilities with the following IDs: %{ids}',
    ),
  },
  getIdFromGraphQLId,
};
</script>

<template>
  <gl-collapse :visible="visible">
    <div>
      <gl-alert v-if="hasError" variant="danger" :dismissible="false">
        <gl-sprintf :message="$options.i18n.vulnerabilitiesUpdateFailed">
          <template #ids>
            <span v-for="({ id, vulnerabilityPath }, index) in rejectedVulnerabilities" :key="id">
              <template v-if="index > 0">,</template>
              <gl-link :href="vulnerabilityPath">{{ $options.getIdFromGraphQLId(id) }}</gl-link>
            </span>
          </template>
        </gl-sprintf>
      </gl-alert>

      <div
        class="gl-mt-5 gl-flex gl-flex-wrap gl-items-start gl-gap-3 gl-border-b-1 gl-border-b-default gl-bg-subtle gl-p-5 gl-border-b-solid"
      >
        <div class="gl-border-r gl-mr-3 gl-mt-3 gl-pr-5" data-testid="selected-vulnerabilities">
          <gl-sprintf :message="$options.i18n.selected">
            <template #count>
              <span class="gl-font-bold">{{ selectedVulnerabilities.length }}</span>
            </template>
          </gl-sprintf>
        </div>

        <gl-collapsible-listbox
          v-if="showActionDropdown"
          v-model="selectedAction"
          :items="actions"
          :toggle-text="toggleText"
          data-testid="select-action-listbox"
        />

        <bulk-change-status
          v-if="showBulkChangeStatus"
          :selected-vulnerabilities="selectedVulnerabilities"
          @cancel="cancel"
          @vulnerabilities-updated="$emit('vulnerabilities-updated', $event)"
          @update-rejected="handleRejected"
          @clear-rejected="rejectedVulnerabilities = []"
        />

        <attach-existing-issue
          v-else-if="showAttachExisting"
          :selected-vulnerabilities="selectedVulnerabilities"
          @cancel="cancel"
          @vulnerabilities-updated="$emit('vulnerabilities-updated', $event)"
          @update-rejected="handleRejected"
          @clear-rejected="rejectedVulnerabilities = []"
        />

        <bulk-change-severity
          v-if="showChangeSeverity"
          :selected-vulnerabilities="selectedVulnerabilities"
          @cancel="cancel"
          @vulnerabilities-updated="$emit('vulnerabilities-updated', $event)"
          @update-rejected="handleRejected"
          @clear-rejected="rejectedVulnerabilities = []"
        />

        <attach-new-issue
          v-if="showAttachNew"
          :selected-vulnerabilities="selectedVulnerabilities"
          @cancel="cancel"
          @vulnerabilities-updated="$emit('vulnerabilities-updated', $event)"
          @update-rejected="handleRejected"
          @clear-rejected="rejectedVulnerabilities = []"
        />
      </div>
    </div>
  </gl-collapse>
</template>
